import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeftIcon, QuestionIcon } from "@phosphor-icons/react";

import Icon from "components/Icon/Icon";
import { CampaignContainer } from "pages/Marketplace/Campaign/styled";
import Button from "components/Buttons";
import {
  UserInformationSection,
  UserProfileInfoContainer
} from "pages/Users/UserProfileInfo/styled";
import CommonTable from "components/Tables/CommonTable";
import usageHistoryColumns from "./usageHistoryColumns";
import SubscriptionPlanModal from "../Modals/SubscriptionPlanModal";
import { useCreatorUsersSlice } from "../slice";
import { useDispatch, useSelector } from "react-redux";
import { getCreatorUserDetails } from "../slice/selectors";

import { useCreatorSubscriptionSlice } from "pages/CreatorUsers/CreatorSubscriptionDetails/slice";
import {
  getCreatorSubscriptionDetails,
  getChangePlanStatus
} from "./slice/selector";
import StatusPillButtons from "components/Buttons/StatusPillButtons";
import CommonTooltip from "components/Tooltip";
import { useDropdownSlice } from "store/dropdownOptions/slice";
import { selectCreatorPlans } from "store/dropdownOptions/slice/selector";

const CreatorSubscriptionDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id, subscriptionId } = useParams();
  const [searchParams] = useSearchParams();

  const { actions: creatorUsersActions } = useCreatorUsersSlice();
  const { actions: creatorSubscriptionActions } = useCreatorSubscriptionSlice();
  const { actions: dropdownActions } = useDropdownSlice();

  const { user: creatorUser } = useSelector(getCreatorUserDetails);
  const {
    loading,
    subscription,
    usageHistory,
    currentLimit,
    pagination,
    currentAdmin
  } = useSelector(getCreatorSubscriptionDetails);
  const {
    loading: changePlanLoading,
    success: changePlanSuccess,
    subscriptionId: newSubscriptionId
  } = useSelector(getChangePlanStatus);
  const { data: creatorPlans, loading: creatorPlansLoading } =
    useSelector(selectCreatorPlans);

  // ===== Fetch dropdown data =====
  useEffect(() => {
    dispatch(dropdownActions.getCreatorPlansRequest());
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(creatorUsersActions.fetchCreatorUserByIdRequest({ userId: id }));
    }
  }, [id]);

  useEffect(() => {
    if (subscriptionId) {
      dispatch(
        creatorSubscriptionActions.fetchCreatorSubscriptionDetailsRequest({
          subscriptionId,
          page: 1,
          limit: 10
        })
      );
    }
  }, [subscriptionId]);

  const tableData = useMemo(() => {
    return [...(currentLimit ? [currentLimit] : []), ...(usageHistory || [])];
  }, [currentLimit, usageHistory]);

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const tableColumns = useMemo(() => usageHistoryColumns(), []);

  const planName = subscription?.plan?.name; // "paid" | "free"
  const isPaidPlan = planName === "paid";

  useEffect(() => {
    if (changePlanSuccess && newSubscriptionId) {
      setShowPlanModal(false);

      navigate(
        `/creator-users/${id}/subscription/${newSubscriptionId}?${searchParams.toString()}`,
        {
          replace: true
        }
      );
    }
  }, [changePlanSuccess, newSubscriptionId, id, searchParams]);

  const handleConfirmChangePlan = (reason) => {
    dispatch(
      creatorSubscriptionActions.changePlanRequest({
        creator_user_id: id,
        subscriptionId,
        plan: selectedPlan?.label,
        deactivation_reason: reason
      })
    );
  };

  const currentPlanId = subscription?.plan?.id;

  const subscriptionMenuItems = useMemo(() => {
    return (creatorPlans || [])
      .filter((plan) => plan.id !== currentPlanId)
      .map((plan) => ({
        label: plan.name,
        value: plan.name,
        plan
      }));
  }, [creatorPlans, currentPlanId]);

  const adminDetails =
    subscription?.status === "active"
      ? subscription?.activated_by_admin
      : subscription?.deactivated_by_admin;

  return (
    <CampaignContainer>
      <UserProfileInfoContainer>
        <div className="user-info-header">
          <div className="header-left-section">
            <figure
              onClick={() =>
                navigate(`/creator-users?${searchParams.toString()}`)
              }
            >
              <Icon component={ArrowLeftIcon} />
            </figure>
            <h2>
              {creatorUser?.first_name} {creatorUser?.last_name} - Subscription
              Management
            </h2>
          </div>
        </div>

        <div className="profile-information-container subscription">
          <UserInformationSection>
            <div className="section-header">
              <h3 className="section-title">Current Subscription</h3>

              {subscription?.status === "active" && (
                <Button
                  onClick={() => setShowPlanModal(true)}
                  disabled={loading}
                  size="S"
                >
                  Modify Plan
                </Button>
              )}
            </div>

            <ul className="user-details-grid">
              <li>
                <span>Current Plan</span>
                <p>{planName || "-"}</p>
              </li>

              <li>
                <span>Start Date</span>
                <p>
                  {subscription?.start_date
                    ? format(new Date(subscription.start_date), "dd MMM yyyy")
                    : "-"}
                </p>
              </li>

              <li>
                <span>End Date</span>
                <p>
                  {subscription?.end_date
                    ? format(new Date(subscription.end_date), "dd MMM yyyy")
                    : "-"}
                </p>
              </li>

              <li>
                <span>Status</span>
                <StatusPillButtons status={subscription?.status} />
              </li>

              {(subscription?.status === "active" ||
                subscription?.status === "expired") && (
                <li>
                  <span>Activated By </span>
                  <p>{adminDetails?.full_name || "-"}</p>
                </li>
              )}

              {subscription?.status === "deactivated" && (
                <li>
                  <span className="common-label">
                    Deactivated By
                    {subscription?.deactivation_reason.length > 0 && (
                      <CommonTooltip
                        theme="dark"
                        tooltipContent={subscription?.deactivation_reason}
                      >
                        <Icon component={QuestionIcon} />
                      </CommonTooltip>
                    )}
                  </span>
                  <p>{adminDetails?.full_name || "-"}</p>
                </li>
              )}

              <li>
                <span>Proposal Limit (Per 7-Day Cycle)</span>
                <p>{subscription?.plan?.proposal_limit}</p>
              </li>

              <li>
                <span>Total Proposals Used</span>
                <p>{subscription?.total_proposal_used}</p>
              </li>

              <li>
                <span>Total Cycles Completed</span>
                <p>{subscription?.total_cycles_completed}</p>
              </li>

              <li>
                <span>Total Limits Reached</span>
                <p>{subscription?.total_limit_reached_count}</p>
              </li>
            </ul>
          </UserInformationSection>

          <UserInformationSection>
            <h3 className="section-title">Usage History</h3>

            <CommonTable
              data={tableData}
              columns={tableColumns}
              total_count={pagination?.totalPages}
              loading={loading}
              initialLoading={loading}
              page={1}
              limit={10}
              tableWrapperStyles="table-wrapper-styles"
              tableClassName="subscription-usage-history-table"
            />
          </UserInformationSection>
        </div>
      </UserProfileInfoContainer>

      <SubscriptionPlanModal
        show={showPlanModal}
        onHide={() => setShowPlanModal(false)}
        currentPlan={planName}
        loading={changePlanLoading}
        onConfirm={handleConfirmChangePlan}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        subscriptionMenuItems={subscriptionMenuItems}
      />
    </CampaignContainer>
  );
};

export default CreatorSubscriptionDetails;
